import Carousel from '@allesauto/core/carousel';
import ProductCarouselFinalItem, {
  ProductCarouselFinalItemProp,
} from '@allesauto/core/productCarouselFinalItem';
import ProductTeaserMinimal, {
  ProductTeaserMinimalProps,
} from '@allesauto/core/productTeaserMinimal';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { HeadlineVariation } from '@allesauto/utils/consts';
import { useNumberOfItemsRow } from '@allesauto/utils/hooks';
import clsx from 'clsx';
import Link from 'next/link';
import React, { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import FinanceDetailsOverlay from '@/components/forms/FinanceDetailsOverlay/FinanceDetailsOverlay';

import { closeOverlay, openOverlay } from '@/store/redux/actions/app';

export interface ProductsCarouselCarsProps {
  products: ProductTeaserMinimalProps[];
  headlineKeyTranslation?: string;
  headlineType: HeadlineVariation;
  typeTestId?: string;
  isHeadlineKeyTranslation?: boolean;
  closingTile?: ProductCarouselFinalItemProp[];
  buttonLabel?: string;
  totalCount?: number;
  buttonLink?: string;
}

const ProductsCarousel: FC<
  React.PropsWithChildren<ProductsCarouselCarsProps>
> = ({
  products,
  headlineKeyTranslation,
  typeTestId,
  headlineType,
  isHeadlineKeyTranslation = true,
  closingTile,
  totalCount,
  buttonLink,
  buttonLabel,
}) => {
  const intl = useIntl();
  const [isMoving, setIsMoving] = useState(false);
  const numberOfItemsRow = useNumberOfItemsRow();
  const dispatch = useDispatch();
  const whiteLabel = useWhiteLabel();

  const classNames = {
    container: clsx(
      'col-end-25 col-start-1 flex flex-col sm:items-stretch md:items-center'
    ),
    button: clsx(
      'bg-red60 font-regular font-weight-bold relative rounded-default text-white sm:bottom-[50px] sm:p-[10px] sm:w-full md:bottom-[70px] md:p-15px md:w-[400px]'
    ),
  };

  const handleCarouselMoving = (movingState: boolean) => {
    setIsMoving(movingState);
  };

  const openOverlayFinance = useCallback(
    (
      summaryText: string,
      legalText: string,
      financeType: SchemaTypes.PaymentType,
      vehicleName: string
    ) =>
      dispatch(
        openOverlay({
          title: translate(intl, `ui.PDP.FOOTER.FINANCE_TYPE.${financeType}`),
          children: (
            <FinanceDetailsOverlay
              legalText={legalText}
              summaryText={summaryText}
              closeOverlay={() => dispatch(closeOverlay())}
              vehicleName={vehicleName}
              financeTypeText={translate(
                intl,
                `ui.FINANCE_DETAILS.${financeType}`
              )}
            />
          ),
        })
      ),
    [dispatch, intl]
  );
  const defaultLabel = translate(
    intl,
    `ui.COMPONENTS.CAROUSEL.BUTTON_LABEL`
  ).replace('{count}', totalCount as unknown as string);

  const buttonLabelWithCount = buttonLabel
    ? buttonLabel.replace('{count}', totalCount as unknown as string)
    : defaultLabel;
  return (
    <div className={classNames.container} data-hide-for-print="">
      <Carousel
        headlineTag={headlineType}
        headline={
          isHeadlineKeyTranslation
            ? headlineKeyTranslation && translate(intl, headlineKeyTranslation)
            : headlineKeyTranslation && headlineKeyTranslation
        }
        isInfinite={false}
        handleAfterChange={handleCarouselMoving}
        handleBeforeChange={handleCarouselMoving}
        classNames={{
          sectionClass: 'bg-white',
          containerClass: products.length === 0 ? 'justify-center' : '',
          dotListClass: ' bg-white mb-100px gap-10px p-30px ',
        }}
        typeTestId={typeTestId}
        productCount={products.length}
      >
        {products &&
          products.map((product) => (
            <ProductTeaserMinimal
              {...product}
              key={product.id}
              isCarouselMoving={isMoving}
              openOverlay={openOverlayFinance}
              numberOfItemsOnRow={numberOfItemsRow}
              isWhiteLabel={!!whiteLabel}
            />
          ))}
        {closingTile &&
          closingTile.map((tile) => (
            <ProductCarouselFinalItem
              key={tile._uid}
              _uid={tile._uid}
              heading={tile.heading}
              content={tile.content}
              linkLabel={tile.linkLabel}
              linkUrl={tile.linkUrl}
            />
          ))}
      </Carousel>

      {products && buttonLink && (
        <Link href={buttonLink || '/'} className="sm:px-15px">
          <button className={classNames.button}>{buttonLabelWithCount}</button>
        </Link>
      )}
    </div>
  );
};

export default ProductsCarousel;
